import { Component, type ReactNode } from 'react';
import { AppError } from '@pointfive/contracts';
import { resetDemoData } from '@/client/DataProvider';

/** The authenticated dataset failed to load, and the product says so instead of disappearing.
 *
 *  This exists because of a failure the journeys found from outside. The aggregate is one `Promise.all` over roughly
 *  thirty procedures, behind a `Suspense` with a `null` fallback and — until now — no error boundary anywhere in the
 *  app. So a single degraded procedure rejected the whole promise, React unwound the tree to nothing, and the result
 *  was an empty `#root`: no screen, no message, no console error, indefinitely.
 *
 *  That is not a hypothetical state. `analyse.analytics.metrics` refuses with `UPSTREAM_DEGRADED` until the
 *  aggregates maintainer has run, and refusing is *correct* — rendering zeros there would assert figures nobody
 *  computed. But it means a brand-new account, before its first maintenance tick, is exactly the account that sees a
 *  blank page. A person in that state cannot tell a degraded upstream from a broken build, and neither could I: it
 *  took an hour with devtools to find, and a tester would simply have reported that the app does not work.
 *
 *  What it renders is the server's own sentence, not a replacement for the data. Nothing here invents a figure or a
 *  screen: it is the same token vocabulary the shell and `/signin` already use, and the same rule applies — this
 *  surface has no pixel authority behind it, which makes the constraint stricter rather than looser (ESC-005).
 *
 *  Scoped to the authenticated tree deliberately. `/signin`, `/card/:token` and `/portal` do not read this aggregate,
 *  and a failure of the trader's own data must not take down the surfaces a person can reach without an account.
 */

interface State {
  readonly error: Error | null;
}

export class DataErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  /** Clearing the memoised promise is what makes the retry a real retry: without it the boundary would re-render
   *  straight back into the same settled rejection and the button would do nothing at all. */
  private readonly retry = () => {
    resetDemoData();
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    // The server's own words when it gave any. `AppError` carries a sentence written for the person reading it;
    // anything else is an unexpected failure and is not dressed up as a considered one.
    const app = error instanceof AppError ? error : null;
    const message = app ? app.message : 'Something went wrong while loading your data. This is on our side.';
    const source = app?.shape && 'source' in app.shape ? String(app.shape.source ?? '') : '';

    return (
      <div
        data-page="dataerror"
        style={{
          minHeight: '100vh',
          background: 'var(--bg)',
          color: 'var(--text)',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 16px',
        }}
      >
        <div
          style={{ width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', gap: '18px' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700, letterSpacing: '-0.01em' }}>PointFive OS</div>
            <div style={{ fontSize: '10.5px', color: 'var(--muted)' }}>Trading operating system</div>
          </div>

          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--card-line)',
              boxShadow: 'var(--shadow-card)',
              borderRadius: '16px',
              padding: '22px 22px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '-0.01em' }}>
                Your data could not be loaded
              </div>
              <div
                data-dataerror-message
                style={{ fontSize: '11.5px', color: 'var(--text-mid)', lineHeight: 1.5 }}
              >
                {message}
              </div>
            </div>

            {/* The code is what someone reports; the source is what an engineer needs, and it names an internal
                component, so it stays on the dev surfaces rather than being shown to a tester. */}
            {app ? (
              <div data-dataerror-code={app.shape.code} style={{ fontSize: '10.5px', color: 'var(--muted)' }}>
                {app.shape.code}
                {__DEV_SURFACES__ && source ? ` · ${source}` : ''}
              </div>
            ) : null}

            <button
              type="button"
              data-dataerror-retry
              onClick={this.retry}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: '11px 18px',
                borderRadius: '10px',
                border: '1px solid color-mix(in srgb, var(--acc) 55%, transparent)',
                background: 'color-mix(in srgb, var(--acc) 12%, transparent)',
                color: 'var(--acc)',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
